import { Children, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { Camera, Copy, Crosshair, Download, ExternalLink, Maximize2, Minimize2, Megaphone, RotateCcw, Sparkles } from "lucide-react";
import { LINKEDIN_FOLD, LINKEDIN_LIMIT, SOCIAL_LANDING_PAGES, X_LIMIT, bannedWordsIn, socialLink, xLength, type SocialNetwork } from "@metrickle/schema/social";
import { RELEASES, type Release, type ReleaseItem } from "../../guides/releases";
import { ApiError, api, useApps } from "../../lib/api";
import { authClient } from "../../lib/auth";
import { captureFrameName } from "../../lib/capture";
import { describeElement, meaningfulBlock, renderCard, shootElement, shootScreen, type Shot } from "../../lib/share-shot";
import { useWorkspace } from "../../lib/support";
import { Badge, Button, ButtonLink, Card, CardHeader, Checkbox, CopyButton, Empty, Field, Input, PageHeader, Segmented, SelectField, Textarea, cx, notifyError, toaster } from "../../components/ui";
import { usePageTitle } from "./components";
import { CARD_FORMATS, ShareCard, type CardFormat, type CardFraming, type CardTheme } from "./share-card";

/**
 * Release posts: every item in "What's new" (guides/releases.tsx) becomes an X post, a LinkedIn
 * post and a share card photographed from the live dashboard. Nothing is posted from here; the
 * founder copies, downloads and posts. The rules the drafts follow are in docs/SOCIAL.md.
 */

interface Entry {
  key: string;
  release: Release;
  item: ReleaseItem;
}

const ENTRIES: Entry[] = RELEASES.flatMap((release) => release.items.map((item, i) => ({ key: `${release.id}#${i}`, release, item })));

/** A release item's `details` as plain text, so it can go to Claude and into a fallback draft. */
function nodeText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) {
    const inner = Children.toArray(node.props.children).map(nodeText).join("");
    if (node.type === "li") return `- ${inner.trim()}\n`;
    if (node.type === "p") return `${inner.trim()}\n`;
    return inner;
  }
  return "";
}

const fmtDate = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

interface Draft {
  x: string;
  linkedin: string;
  landingPage: string;
  altText: string;
  steer: string;
  drafted: "template" | "claude";
}

/** Before Claude has been asked (or when it isn't set up): the item's own words, in the right shape. */
function templateDraft(e: Entry): Draft {
  const firstSentence = e.item.body.match(/^.*?[.!?](\s|$)/)?.[0].trim() ?? e.item.body;
  let x = `Shipped this week: ${e.item.title}.\n\n${firstSentence}`;
  if (xLength(x) > X_LIMIT) x = `Shipped this week: ${e.item.title}.`;
  const details = e.item.details ? `\n\n${nodeText(e.item.details).trim()}` : "";
  return {
    x,
    linkedin: `New in Metrickle: ${e.item.title}.\n\n${e.item.body}${details}\n\nIt's free for three apps while we're in early access. What would you use it for?\n\n#CRO #ProductAnalytics #WebAccessibility`,
    landingPage: "/",
    altText: "",
    steer: "",
    drafted: "template",
  };
}

const STORE = "mk-social:";
function loadDraft(e: Entry): Draft {
  try {
    const raw = localStorage.getItem(STORE + e.key);
    if (raw) return { ...templateDraft(e), ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    /* storage blocked or old shape */
  }
  return templateDraft(e);
}
function saveDraft(key: string, d: Draft) {
  try {
    localStorage.setItem(STORE + key, JSON.stringify(d));
  } catch {
    /* storage blocked: the draft only lives until reload */
  }
}

export function AdminSocial() {
  usePageTitle("Release posts");
  const [key, setKey] = useState(ENTRIES[0]?.key);
  const entry = ENTRIES.find((e) => e.key === key);

  return (
    <>
      <PageHeader
        title="Release posts"
        subtitle="Each update in What's new as an X post, a LinkedIn post and a share card shot from the live dashboard. Nothing is posted from here."
      />
      <div className="grid gap-4 @4xl:grid-cols-[260px_minmax(0,1fr)]">
        <nav aria-label="Updates" className="@4xl:sticky @4xl:top-4 @4xl:self-start">
          <Card className="p-2">
            <ul className="space-y-0.5">
              {RELEASES.map((r) => (
                <li key={r.id}>
                  <p className="px-2 pt-2 pb-1 text-xs font-medium text-ink-3">{fmtDate(r.date)}</p>
                  <ul>
                    {r.items.map((item, i) => {
                      const k = `${r.id}#${i}`;
                      return (
                        <li key={k}>
                          <button
                            type="button"
                            aria-current={k === key ? "true" : undefined}
                            onClick={() => setKey(k)}
                            className={cx(
                              "w-full rounded-md px-2 py-1.5 text-left text-sm",
                              k === key ? "bg-accent-wash font-medium text-accent-text" : "text-ink-2 hover:bg-surface-2 hover:text-ink",
                            )}
                          >
                            {item.title}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </Card>
        </nav>
        {entry ? <Composer key={entry.key} entry={entry} /> : (
          <Card>
            <Empty title="No updates in What's new yet" icon={<Megaphone size={24} aria-hidden />} />
          </Card>
        )}
      </div>
    </>
  );
}

function Composer({ entry }: { entry: Entry }) {
  const [draft, setDraftState] = useState(() => loadDraft(entry));
  const setDraft = (patch: Partial<Draft>) =>
    setDraftState((d) => {
      const next = { ...d, ...patch };
      saveDraft(entry.key, next);
      return next;
    });
  const [cardBlob, setCardBlob] = useState<Blob | null>(null);
  const [cardDataUrl, setCardDataUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const draftWithClaude = async () => {
    setBusy(true);
    try {
      const r = await api<{ x: string; linkedin: string; landingPage: string; altText: string }>("/admin/social/draft", {
        method: "POST",
        json: {
          title: entry.item.title,
          body: entry.item.body,
          details: entry.item.details ? nodeText(entry.item.details).trim().slice(0, 3000) : undefined,
          date: entry.release.date,
          instructions: draft.steer.trim() || undefined,
          image: cardDataUrl ?? undefined,
        },
      });
      setDraft({ ...r, altText: r.altText || draft.altText, drafted: "claude" });
      toaster.success({ title: "Drafted. Read it through before posting." });
    } catch (err) {
      if (err instanceof ApiError && err.status === 501) toaster.error({ title: "Claude isn't set up", description: "Set ANTHROPIC_API_KEY on the API. The drafts below are from the update's own words." });
      else notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-w-0 space-y-4">
      <Card>
        <CardHeader
          title={entry.item.title}
          subtitle={`What's new, ${fmtDate(entry.release.date)}`}
          actions={entry.item.link && <Badge>{entry.item.link.to}</Badge>}
        />
        <p className="px-4 pb-4 text-sm text-ink-2">{entry.item.body}</p>
      </Card>

      <CardStudio
        entry={entry}
        onRendered={(blob, url) => {
          setCardBlob(blob);
          setCardDataUrl(url);
        }}
        cardBlob={cardBlob}
        altText={draft.altText}
        onAltText={(altText) => setDraft({ altText })}
      />

      <Card>
        <CardHeader
          title="Posts"
          subtitle={
            draft.drafted === "claude"
              ? "Drafted by Claude from the update and the card. Edits are kept in this browser."
              : "From the update's own words. Draft with Claude for posts that follow docs/SOCIAL.md."
          }
          actions={
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDraftState(() => { const d = templateDraft(entry); saveDraft(entry.key, d); return d; })}>
                <RotateCcw size={14} aria-hidden /> Start over
              </Button>
              <Button size="sm" variant="primary" onClick={draftWithClaude} disabled={busy} aria-busy={busy}>
                <Sparkles size={14} aria-hidden /> {busy ? "Drafting…" : "Draft with Claude"}
              </Button>
            </div>
          }
        />
        <div className="space-y-5 px-4 pb-4">
          <Field label="Steer for Claude (optional)" hint={cardDataUrl ? "Claude also sees the card you rendered, and writes its alt text." : "Render the card first and Claude writes its alt text too."}>
            <Input value={draft.steer} onChange={(e) => setDraft({ steer: e.target.value })} placeholder="e.g. lead with agencies running client apps" maxLength={1000} />
          </Field>
          <div className="grid gap-5 @2xl:grid-cols-2">
            <PostEditor network="x" draft={draft} setDraft={setDraft} />
            <PostEditor network="linkedin" draft={draft} setDraft={setDraft} />
          </div>
        </div>
      </Card>
    </div>
  );
}

// --- The card ----------------------------------------------------------------------------------

/** localStorage key: the workspace last photographed. */
const SHOT_WORKSPACE = "mk-share-workspace";
const FRAME_W = 1440;
const FRAME_H = 900;

function CardStudio({
  entry,
  onRendered,
  cardBlob,
  altText,
  onAltText,
}: {
  entry: Entry;
  onRendered: (blob: Blob, dataUrl: string) => void;
  cardBlob: Blob | null;
  altText: string;
  onAltText: (v: string) => void;
}) {
  // The frame can show any of your workspaces without switching yours, so a demo workspace with
  // example data can be photographed while you work in your own. The choice is remembered.
  const workspace = useWorkspace();
  const orgs = authClient.useListOrganizations();
  const [wsId, setWsId] = useState<string | undefined>(() => {
    try {
      return localStorage.getItem(SHOT_WORKSPACE) ?? undefined;
    } catch {
      return undefined;
    }
  });
  const ws = (wsId && orgs.data?.some((o) => o.id === wsId) ? wsId : undefined) ?? workspace.id;
  const apps = useApps(ws);
  const [appId, setAppId] = useState<string>();
  const app = appId ?? apps.data?.[0]?.id;
  const linkPath = entry.item.link?.to ?? "/";
  const resolved = linkPath.includes(":appId") ? (app ? linkPath.replace(":appId", app) : null) : linkPath;
  // What the frame was asked to open, and where it is now (people click around inside it). Kept
  // apart so a page finishing loading never sends the frame back to it.
  const [path, setPath] = useState<string | null>(null);
  const framePath = path ?? resolved;
  const [shown, setShown] = useState<string | null>(null);
  /** What's typed in the Screen box, until it's opened; the frame moving doesn't overwrite it. */
  const [typed, setTyped] = useState<string | null>(null);

  const [theme, setTheme] = useState<CardTheme>("light");
  const [framing, setFraming] = useState<CardFraming>("screen");
  const [format, setFormat] = useState<CardFormat>("landscape");
  const [headline, setHeadline] = useState(entry.item.title);
  const [kicker, setKicker] = useState(`New · ${new Date(`${entry.release.date}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`);
  const [exampleData, setExampleData] = useState(true);
  const [screen, setScreen] = useState<Shot | null>(null);
  const [closeup, setCloseup] = useState<Shot | null>(null);
  const [shooting, setShooting] = useState(false);

  const frame = useRef<HTMLIFrameElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const picker = usePicker(frame);
  const ids = { frame: useId(), alt: useId() };

  // The framed dashboard follows the card's theme, and a new page drops whatever was picked.
  const applyTheme = useCallback(() => {
    const doc = frame.current?.contentDocument;
    if (doc?.documentElement) doc.documentElement.dataset.theme = theme;
  }, [theme]);
  useEffect(applyTheme, [applyTheme]);

  const shoot = async () => {
    const win = frame.current?.contentWindow;
    if (!win) return;
    setShooting(true);
    try {
      picker.setPicking(false);
      // Let hover states and the picker outline clear before the shot. A timer, not a frame: frames
      // don't come while the tab is in the background, and the shot would never start.
      await new Promise((r) => setTimeout(r, 60));
      setScreen(await shootScreen(win));
      if (framing === "closeup") {
        if (!picker.picked) {
          toaster.error({ title: "Pick part of the screen first", description: "Choose “Pick a close-up”, then click the part of the screen to feature." });
        } else setCloseup(await shootElement(picker.picked));
      }
    } catch (err) {
      notifyError(err);
    } finally {
      setShooting(false);
    }
  };

  const render = async () => {
    if (!card.current) return null;
    const blob = await renderCard(card.current);
    const url = await new Promise<string>((res) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result as string);
      fr.readAsDataURL(blob);
    });
    onRendered(blob, url);
    return blob;
  };

  const filename = `metrickle-${entry.release.id}-${entry.item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${format}.png`;
  const download = async () => {
    try {
      const blob = await render();
      if (!blob) return;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) {
      notifyError(err);
    }
  };
  const copyImage = async () => {
    try {
      // Safari wants the ClipboardItem made inside the click, with the image still a promise.
      await navigator.clipboard.write([new ClipboardItem({ "image/png": render().then((b) => b ?? Promise.reject(new Error("No card"))) })]);
      toaster.success({ title: "Card copied" });
    } catch (err) {
      notifyError(err);
    }
  };

  const fmt = CARD_FORMATS[format];
  const preview = useFitScale(fmt.width);
  const stage = useFitScale(FRAME_W);
  const hasShot = framing === "screen" ? !!screen : !!closeup;

  return (
    <Card>
      <CardHeader title="Share card" subtitle="Photograph a screen of the dashboard, or one part of it, and frame it for the feed." />
      <div className="space-y-5 px-4 pb-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Workspace">
            <SelectField
              aria-label="Workspace to photograph"
              items={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
              value={ws}
              onChange={(v) => {
                setWsId(v);
                try {
                  localStorage.setItem(SHOT_WORKSPACE, v);
                } catch {
                  /* storage blocked */
                }
                setAppId(undefined);
                setPath(null);
                setShown(null);
                setTyped(null);
              }}
              className="w-56"
            />
          </Field>
          <Field label="App">
            <SelectField
              aria-label="App to photograph"
              items={(apps.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
              value={app}
              onChange={(v) => {
                setAppId(v);
                setPath(null);
                setShown(null);
                setTyped(null);
              }}
              className="w-56"
            />
          </Field>
          <div className="min-w-64 flex-1">
            <Field label="Screen">
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const v = typed?.trim();
                  if (v?.startsWith("/")) {
                    setPath(v);
                    setShown(v);
                  }
                  setTyped(null);
                }}
              >
                <Input name="path" value={typed ?? shown ?? framePath ?? ""} onChange={(e) => setTyped(e.target.value)} aria-describedby={ids.frame} spellCheck={false} className="font-mono text-[13px]" />
                <Button type="submit">Open</Button>
              </form>
            </Field>
          </div>
        </div>
        <p id={ids.frame} className="text-xs text-ink-3">
          Everything in the shot goes public. Use a workspace with example or your own data, never a customer's. You can click around inside the screen below, and open dialogs and menus to photograph them.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <Segmented aria-label="Framing" items={[{ value: "screen", label: "Whole screen" }, { value: "closeup", label: "Close-up" }]} value={framing} onChange={(v) => setFraming(v as CardFraming)} />
          <Segmented aria-label="Theme" items={[{ value: "light", label: "Light" }, { value: "dark", label: "Dark" }]} value={theme} onChange={(v) => setTheme(v as CardTheme)} />
          {framing === "closeup" && (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant={picker.picking ? "primary" : "secondary"} aria-pressed={picker.picking} onClick={() => picker.setPicking(!picker.picking)}>
                <Crosshair size={14} aria-hidden /> {picker.picking ? "Picking… (Esc to stop)" : "Pick a close-up"}
              </Button>
              <Button size="sm" variant="ghost" onClick={picker.pickFocused} title="Tab into the screen to an element, then use this">
                Use focused element
              </Button>
              <Button size="sm" variant="ghost" onClick={picker.wider} disabled={!picker.picked}>
                <Maximize2 size={14} aria-hidden /> Wider
              </Button>
              <Button size="sm" variant="ghost" onClick={picker.narrower} disabled={!picker.canNarrow}>
                <Minimize2 size={14} aria-hidden /> Narrower
              </Button>
            </div>
          )}
          <Button variant="primary" onClick={shoot} disabled={shooting || !framePath} aria-busy={shooting} className="ml-auto">
            <Camera size={15} aria-hidden /> {shooting ? "Taking shot…" : "Take shot"}
          </Button>
        </div>
        <p className="text-xs text-ink-2" aria-live="polite">
          {framing === "closeup" ? (picker.picked ? `Picked ${picker.label}.` : "Nothing picked yet.") : "The shot is what the screen below shows, scrolled where it is."}
        </p>

        <div ref={stage.ref} className="overflow-hidden rounded-lg border border-line bg-surface-2" style={{ height: FRAME_H * stage.scale }}>
          <div style={{ width: FRAME_W, height: FRAME_H, transform: `scale(${stage.scale})`, transformOrigin: "top left", position: "relative" }}>
            {framePath ? (
              <iframe
                // A new workspace is a new frame: the dashboard reads which one to show as it starts.
                key={ws}
                ref={frame}
                name={captureFrameName(ws !== workspace.id ? ws : undefined)}
                src={framePath}
                title="Dashboard screen to photograph"
                width={FRAME_W}
                height={FRAME_H}
                className="block border-0 bg-bg"
                onLoad={() => {
                  applyTheme();
                  picker.reset();
                  const loc = frame.current?.contentWindow?.location;
                  if (loc) setShown(loc.pathname + loc.search + loc.hash);
                }}
              />
            ) : (
              <p className="p-10 text-2xl text-ink-3">Add an app to this workspace to photograph its screens.</p>
            )}
            {picker.box && (
              <div
                aria-hidden
                className={cx("pointer-events-none absolute rounded-md bg-accent-wash outline-3 outline-offset-3 outline-accent", picker.picking ? "outline-dashed" : "outline-solid")}
                style={{ left: picker.box.x, top: picker.box.y, width: picker.box.w, height: picker.box.h }}
              />
            )}
          </div>
        </div>

        <div className="grid gap-4 @6xl:grid-cols-[minmax(0,1fr)_280px]">
          <div ref={preview.ref} className="min-w-0">
            <div className="overflow-hidden rounded-lg border border-line" style={{ width: fmt.width * preview.scale, height: fmt.height * preview.scale }}>
              <div role="img" aria-label={altText || `Share card: ${headline}`} style={{ width: fmt.width, height: fmt.height, transform: `scale(${preview.scale})`, transformOrigin: "top left" }}>
                <ShareCard ref={card} format={format} theme={theme} framing={framing} headline={headline} kicker={kicker} screen={screen} closeup={closeup} exampleData={exampleData} />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <Field label="Size">
              <SelectField
                aria-label="Card size"
                items={Object.entries(CARD_FORMATS).map(([value, f]) => ({ value, label: f.label }))}
                value={format}
                onChange={(v) => setFormat(v as CardFormat)}
                className="w-full"
              />
            </Field>
            <Field label="Headline">
              <Input value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={120} />
            </Field>
            <Field label="Label">
              <Input value={kicker} onChange={(e) => setKicker(e.target.value)} maxLength={40} />
            </Field>
            <Checkbox checked={exampleData} onCheckedChange={setExampleData} hint="Required unless the numbers are ours or a customer agreed.">
              Label as example data
            </Checkbox>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="primary" onClick={download} disabled={!hasShot}>
                <Download size={15} aria-hidden /> Download PNG
              </Button>
              <Button onClick={copyImage} disabled={!hasShot}>
                <Copy size={15} aria-hidden /> Copy image
              </Button>
            </div>
            {cardBlob && <p className="text-xs text-ink-3">Rendered at {fmt.width * 2}×{fmt.height * 2}, {Math.round(cardBlob.size / 1024)} KB.</p>}
          </div>
        </div>

        <Field label="Alt text" hint="Paste this into the image description on X and LinkedIn. Say what the shot shows that the post doesn't.">
          <Textarea id={ids.alt} value={altText} onChange={(e) => onAltText(e.target.value)} rows={2} maxLength={1000} />
        </Field>
        {!altText.trim() && hasShot && <p className="text-xs text-bad">Add alt text before posting (docs/SOCIAL.md: every image has it).</p>}
      </div>
    </Card>
  );
}

/** Scales a fixed-width stage down to the width of its container. */
function useFitScale(width: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(Math.min(1, el.clientWidth / width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  return { ref, scale };
}

/**
 * Choosing the part of the framed screen to feature: hover outlines a block, click picks it (the
 * click doesn't reach the page). Wider and Narrower walk up and back down the page structure. For
 * keyboard use, Tab to something inside the screen and use the focused element.
 */
function usePicker(frame: RefObject<HTMLIFrameElement | null>) {
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<HTMLElement | null>(null);
  const [hover, setHover] = useState<HTMLElement | null>(null);
  const trail = useRef<HTMLElement[]>([]);
  const [, bump] = useState(0);

  const pick = useCallback((el: HTMLElement | null, keepTrail = false) => {
    if (!keepTrail) trail.current = [];
    setPicked(el);
  }, []);

  useEffect(() => {
    const win = frame.current?.contentWindow;
    if (!picking || !win) return;
    const move = (e: MouseEvent) => setHover(meaningfulBlock(e.target as Element));
    // The page never sees the press: a menu item would fire on pointerup, and a press would move
    // focus or close an open popover. Listening on the frame's window runs before the page's own
    // document listeners.
    const swallow = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const click = (e: MouseEvent) => {
      swallow(e);
      const el = meaningfulBlock(e.target as Element);
      if (el) {
        pick(el);
        setPicking(false);
      }
    };
    // Escape stops picking, and doesn't also close the dialog being picked from.
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (e.view === win) swallow(e);
      setPicking(false);
    };
    const pressed = ["pointerdown", "pointerup", "mousedown", "mouseup"] as const;
    win.addEventListener("mousemove", move, true);
    win.addEventListener("click", click, true);
    pressed.forEach((t) => win.addEventListener(t, swallow, true));
    win.addEventListener("keydown", key, true);
    window.addEventListener("keydown", key);
    return () => {
      win.removeEventListener("mousemove", move, true);
      win.removeEventListener("click", click, true);
      pressed.forEach((t) => win.removeEventListener(t, swallow, true));
      win.removeEventListener("keydown", key, true);
      window.removeEventListener("keydown", key);
      setHover(null);
    };
  }, [picking, frame, pick]);

  // Keep the outline on the element as the screen scrolls or resizes.
  useEffect(() => {
    const win = frame.current?.contentWindow;
    if (!win || !(picked || hover)) return;
    const on = () => bump((n) => n + 1);
    win.addEventListener("scroll", on, { passive: true });
    win.addEventListener("resize", on);
    return () => {
      win.removeEventListener("scroll", on);
      win.removeEventListener("resize", on);
    };
  }, [picked, hover, frame]);

  const target = picking ? (hover ?? picked) : picked;
  const r = target?.isConnected ? target.getBoundingClientRect() : null;

  return {
    picking,
    setPicking,
    picked: picked?.isConnected ? picked : null,
    label: picked?.isConnected ? describeElement(picked) : "",
    box: r && { x: r.left, y: r.top, w: r.width, h: r.height },
    canNarrow: trail.current.length > 0,
    reset: () => {
      setPicking(false);
      pick(null);
    },
    pickFocused: () => {
      const doc = frame.current?.contentDocument;
      const el = meaningfulBlock(doc?.activeElement ?? null);
      if (el && el !== doc?.body) pick(el);
      else toaster.error({ title: "Nothing focused in the screen", description: "Tab into the screen to the part you want, then try again." });
    },
    wider: () => {
      if (!picked) return;
      // Skip wrappers the same size as what's picked: "Wider" should always show more.
      const r = picked.getBoundingClientRect();
      let parent = picked.parentElement;
      while (parent && parent !== picked.ownerDocument.body) {
        const p = parent.getBoundingClientRect();
        if (p.width > r.width + 4 || p.height > r.height + 4) break;
        parent = parent.parentElement;
      }
      if (!parent || parent === picked.ownerDocument.body) return;
      trail.current.push(picked);
      pick(parent, true);
    },
    narrower: () => {
      const child = trail.current.pop();
      if (child) pick(child, true);
    },
  };
}

// --- Posts -------------------------------------------------------------------------------------

function PostEditor({ network, draft, setDraft }: { network: SocialNetwork; draft: Draft; setDraft: (p: Partial<Draft>) => void }) {
  const isX = network === "x";
  const text = isX ? draft.x : draft.linkedin;
  const length = isX ? xLength(text) : [...text].length;
  const limit = isX ? X_LIMIT : LINKEDIN_LIMIT;
  const over = length > limit;
  const banned = bannedWordsIn(text);
  const link = socialLink(draft.landingPage, network);
  const ids = { count: useId(), warn: useId() };
  const intent = isX
    ? `https://x.com/intent/post?text=${encodeURIComponent(text)}`
    : `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`;
  const foldAt = useMemo(() => (isX ? -1 : Math.min(text.length, LINKEDIN_FOLD)), [isX, text]);

  return (
    <section aria-labelledby={`${ids.count}-h`} className="min-w-0 space-y-3">
      <div className="flex items-center justify-between">
        <h3 id={`${ids.count}-h`} className="text-sm font-semibold text-ink">
          {isX ? "X" : "LinkedIn"}
        </h3>
        <span id={ids.count} className={cx("text-xs tabular-nums", over ? "font-medium text-bad" : "text-ink-3")}>
          {length} / {limit}
          {over && " (too long)"}
        </span>
      </div>
      <Textarea
        aria-label={`${isX ? "X" : "LinkedIn"} post`}
        aria-describedby={`${ids.count}${banned.length ? ` ${ids.warn}` : ""}`}
        aria-invalid={over || undefined}
        value={text}
        onChange={(e) => setDraft(isX ? { x: e.target.value } : { linkedin: e.target.value })}
        rows={isX ? 8 : 14}
        className="leading-relaxed"
      />
      {!isX && (
        <p className="text-xs text-ink-3">
          Shown before “…see more”: <span className="text-ink-2">{text.slice(0, foldAt)}</span>
          {text.length > foldAt && "…"}
        </p>
      )}
      {banned.length > 0 && (
        <p id={ids.warn} className="text-xs text-bad">
          Off-message: {banned.map((b) => `“${b.found}” (say ${b.instead})`).join(", ")}.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <CopyButton text={text} label="Copy post" done="Post copied" variant="secondary" />
        <ButtonLink to={intent} target="_blank" rel="noopener noreferrer" variant="ghost" size="sm">
          <ExternalLink size={14} aria-hidden /> Open in {isX ? "X" : "LinkedIn"}
          <span className="sr-only"> (new tab)</span>
        </ButtonLink>
      </div>
      <div className="rounded-lg bg-surface-2 p-3">
        <p className="text-xs font-medium text-ink">{isX ? "First reply" : "First comment"}: the link</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <SelectField
            aria-label="Page the link goes to"
            items={SOCIAL_LANDING_PAGES.map((p) => ({ value: p.path, label: p.label }))}
            value={draft.landingPage}
            onChange={(v) => setDraft({ landingPage: v })}
            className="h-8"
          />
          <code className="min-w-0 flex-1 truncate text-xs text-ink-2">{link}</code>
          <CopyButton text={link} label="Copy link" done="Link copied" variant="secondary" />
        </div>
        <p className="mt-2 text-xs text-ink-3">Attach the card yourself; {isX ? "X" : "LinkedIn"} can't take an image from a link.</p>
      </div>
    </section>
  );
}
